import { lazy } from "react";

const LAST_RELOAD_KEY = "chunk-load-last-reload";
// Long enough that "reload didn't fix it" failures fall inside the window,
// short enough that a genuinely new deploy later in the session can still
// trigger its own recovery reload.
const RELOAD_COOLDOWN_MS = 30 * 1000;

/**
 * React.lazy wrapper that self-heals after a deploy. Every deploy renames the
 * hashed chunk files, so a tab opened before the deploy can request a chunk
 * that no longer exists and crash. On a chunk-load failure this reloads the
 * page (picking up the new index.html and chunk names), at most once per
 * cooldown window. The timestamp guard (rather than a boolean flag) means a
 * chunk that keeps failing after the reload — e.g. during a network outage
 * where other chunks still load from cache — propagates its error instead of
 * reloading forever.
 */
export const lazyWithRetry = (importFn) =>
  lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      const lastReload =
        Number(window.sessionStorage.getItem(LAST_RELOAD_KEY)) || 0;
      if (Date.now() - lastReload > RELOAD_COOLDOWN_MS) {
        window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        // Keep Suspense pending while the page reloads.
        return new Promise(() => {});
      }
      throw error;
    }
  });
